import { Field } from '@atlaskit/form';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type DatabaseFieldType, type DatabaseField } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../messages.ts';

type UserFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.USER>;
};

const toggleWrapperStyles = xcss({
  marginInline: 'space.negative.075'
});

const inlineStyles = xcss({
  marginBlock: 'space.negative.050'
});

const UserFieldSettings: FunctionComponent<UserFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { formatMessage } = useIntl();
  const label = formatMessage(messages.sendNotificationsToggleLabel);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_MENTION_NOTIFICATION_ENABLED,
          fieldId: field.id,
          mentionNotificationEnabled: event.target.checked
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  return (
    <Field
      id="send-notifications-toggle"
      label={
        <Text size="small" weight="bold" color="color.text.subtle">
          {formatMessage(messages.fieldSettingsNotifyUsersSectionLabel)}
        </Text>
      }
      name="send-notifications-toggle"
    >
      {({ fieldProps }) => (
        <Inline
          alignBlock="center"
          space="space.075"
          spread="space-between"
          xcss={inlineStyles}
        >
          <Text color="color.text.subtle">{label}</Text>
          <Box xcss={toggleWrapperStyles}>
            <Toggle
              id={fieldProps.id}
              isChecked={field.settings.mentionNotificationEnabled}
              label={label}
              onChange={handleChange}
            />
          </Box>
        </Inline>
      )}
    </Field>
  );
};

export default UserFieldSettings;
